import { useState, useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { GoogleLoginButton } from "@/components/GoogleLoginButton";
import { Eye, EyeOff, ChevronDown, ChevronUp, Check, ArrowRight, ShieldCheck } from "lucide-react";
import type { PlanVertical } from "@shared/plan-vertical";
import { useBillingProvider } from "@/hooks/useBillingProvider";

type Tier = "start" | "pro" | "expert";
type Cycle = "monthly" | "annual";
// VP-116: jedno źródło prawdy — patrz packages/shared/plan-vertical.ts.
type Vertical = PlanVertical;

interface SelectedPlan {
  tier: Tier;
  cycle: Cycle;
  vertical: Vertical;
}

const TIER_FROM_PARAM: Record<string, Tier> = {
  starter: "start",
  start: "start",
  pro: "pro",
  expert: "expert",
};

const VALID_VERTICALS = new Set<Vertical>(["lead_generation", "ecommerce", "content_creation"]);

const TIER_DISPLAY: Record<
  Tier,
  { name: string; priceMonthly: string; priceAnnual: string; tagline: string; bullets: string[] }
> = {
  start: {
    name: "Start",
    priceMonthly: "499 zł/msc",
    priceAnnual: "4 990 zł/rok",
    tagline: "Cała maszyna do tworzenia reklam i contentu",
    bullets: [
      "Twórca reklam graficznych",
      "Twórca reklam video",
      "Twórca skryptów reklam",
      "Twórca VSL",
      "Twórca filmów na YouTube",
      "Twórca rolek (IG / TikTok / Shorts)",
      "Twórca strategii marketingowej",
      "Kreator kampanii reklamowych z AI",
      "Analityk AI",
      "Monitoring AI",
      "Wsparcie Intercom",
    ],
  },
  pro: {
    name: "Pro",
    priceMonthly: "1 499 zł/msc",
    priceAnnual: "14 990 zł/rok",
    tagline: "Wszystko ze Start + integracja z CRM",
    bullets: [
      "Wszystkie funkcje z Planu Start",
      "Połączenie z CRM (LeadConnector / HubSpot / Calendly)",
      "Priorytetowe wsparcie Intercom",
    ],
  },
  expert: {
    name: "Expert",
    priceMonthly: "5 999 zł/msc",
    priceAnnual: "59 990 zł/rok",
    tagline: "Wszystko z Pro + pełna ścieżka klienta",
    bullets: [
      "Wszystkie funkcje z Planu Pro",
      "Server-side CAPI (Meta)",
      "Advanced tracking — pokazuje pełną ścieżkę klienta od kliknięcia do kasy",
      "Dedykowany opiekun konta",
      "Priorytetowe wsparcie Intercom z SLA",
    ],
  },
};

function readSelectedPlan(): SelectedPlan | null {
  const params = new URLSearchParams(window.location.search);
  const planParam = params.get("plan");
  if (!planParam) return null;
  const tier = TIER_FROM_PARAM[planParam.toLowerCase()];
  if (!tier) return null;
  const cycleParam = params.get("cycle")?.toLowerCase();
  const cycle: Cycle = cycleParam === "annual" || cycleParam === "yearly" ? "annual" : "monthly";
  const verticalParam = params.get("vertical")?.toLowerCase() ?? "lead_generation";
  const vertical: Vertical = VALID_VERTICALS.has(verticalParam as Vertical)
    ? (verticalParam as Vertical)
    : "lead_generation";
  return { tier, cycle, vertical };
}

export default function CheckoutPage() {
  const [, setLocation] = useLocation();
  const { user, isAuthenticated, register, isRegisterLoading } = useAuth();
  const { isShopifyBilling } = useBillingProvider();
  const { toast } = useToast();

  const selectedPlan = useMemo<SelectedPlan | null>(
    () => (typeof window !== "undefined" ? readSelectedPlan() : null),
    []
  );

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    referralCode: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    if (!selectedPlan) {
      setLocation("/register");
    }
  }, [selectedPlan, setLocation]);

  // VP-329: konto rozliczane przez Shopify (App Store §1.2.1) nie może trafić na
  // nasz checkout — nawet z linka. Backend i tak odrzuca `/api/billing/*` z 409.
  useEffect(() => {
    if (isShopifyBilling) setLocation("/settings?tab=usage");
  }, [isShopifyBilling, setLocation]);

  // Override global body scroll lock so the checkout page can scroll on mobile
  // (matches sales/thank-you pages — see html.allow-scroll rule in index.css).
  useEffect(() => {
    document.documentElement.classList.add("allow-scroll");
    return () => {
      document.documentElement.classList.remove("allow-scroll");
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fpr = params.get("fpr");
    if (fpr) {
      setFormData((prev) => ({ ...prev, referralCode: fpr }));
      setShowReferral(true);
    }
  }, []);

  const startCheckout = async () => {
    if (!selectedPlan) return;
    setIsRedirecting(true);
    try {
      const data = (await apiRequest("POST", "/api/billing/create-checkout", selectedPlan)) as {
        url?: string;
      };
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error("Brak URL Stripe Checkout");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Spróbuj ponownie z poziomu konta.";
      toast({
        title: "Nie udało się otworzyć płatności",
        description: message,
        variant: "destructive",
      });
      setIsRedirecting(false);
    }
  };

  useEffect(() => {
    // `isShopifyBilling` musi być w tym warunku, nie tylko w efekcie przekierowującym:
    // oba efekty lecą w tym samym commicie, więc bez tego merchant z App Store zdążyłby
    // wystrzelić żądanie do Stripe'a (i zobaczyć czerwony toast z 409).
    if (isAuthenticated && user && selectedPlan && !isRedirecting && !isShopifyBilling) {
      void startCheckout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, selectedPlan, isShopifyBilling]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password.length < 8) {
      toast({
        title: "Hasło zbyt krótkie",
        description: "Minimum 8 znaków.",
        variant: "destructive",
      });
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast({ title: "Hasła nie są identyczne", variant: "destructive" });
      return;
    }
    try {
      const hasReferral = Boolean(formData.referralCode);
      await register({
        username: formData.email.split("@")[0],
        email: formData.email,
        password: formData.password,
        ...(hasReferral ? { referralCode: formData.referralCode } : {}),
      });
      // useEffect above triggers startCheckout once auth state flips
    } catch (error) {
      const msg = error instanceof Error ? error.message : "";
      const exists = msg.includes("już istnieje") || msg.toLowerCase().includes("already exists");
      toast({
        title: exists ? "Konto już istnieje" : "Nie udało się utworzyć konta",
        description: exists
          ? "Zaloguj się, a następnie wróć tu, aby dokończyć płatność."
          : msg || "Spróbuj ponownie.",
        variant: "destructive",
      });
    }
  };

  if (isRedirecting) {
    return (
      <div className="h-screen flex items-center justify-center bg-[#030407] text-white">
        <div className="text-center">
          <div className="text-2xl font-bold mb-2">Przekierowuję do płatności…</div>
          <div className="text-sm text-white/60">Zaraz wylądujesz na Stripe Checkout.</div>
        </div>
      </div>
    );
  }

  if (!selectedPlan) {
    return null;
  }

  const display = TIER_DISPLAY[selectedPlan.tier];
  const priceLabel = selectedPlan.cycle === "annual" ? display.priceAnnual : display.priceMonthly;

  return (
    <div className="min-h-screen bg-[#030407] flex flex-col lg:flex-row">
      {/* Left — plan summary (on mobile renders BELOW the form) */}
      <div className="order-2 lg:order-1 lg:flex-1 lg:flex lg:flex-col lg:justify-center px-6 py-12 lg:px-16 xl:px-24">
        <div className="max-w-lg mx-auto lg:mx-0">
          <img
            src="https://pub-9c0a0846a5c24484849bf7e3f13e4239.r2.dev/viralpartners/logo-800w.webp"
            alt="Viral Partners Logo"
            className="h-12 object-contain mb-8"
          />
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-red-500/10 border border-red-500/30 text-red-400 text-xs font-semibold mb-4">
            Wybrany plan
          </div>
          <h1 className="text-heading-1 font-extrabold text-white leading-tight mb-3">
            Plan{" "}
            <span className="bg-gradient-to-r from-red-400 to-red-300 bg-clip-text text-transparent">
              {display.name}
            </span>
          </h1>
          <p className="text-white/60 text-lg mb-2">{display.tagline}</p>
          <div className="text-white text-2xl font-bold mb-8">
            {priceLabel}
            <span className="text-white/40 text-sm font-normal ml-2">netto + 23% VAT</span>
          </div>
          <ul className="space-y-3 mb-8">
            {display.bullets.map((bullet) => (
              <li key={bullet} className="flex items-start gap-3 text-white/80">
                <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
          <div className="p-4 rounded-xl bg-white/[0.03] border border-white/[0.08] flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <p className="text-white/70 text-sm">
              Płatność obsługuje Stripe. Możesz anulować subskrypcję w każdej chwili z poziomu
              ustawień konta.
            </p>
          </div>
        </div>
      </div>
      {/* Right — register + go to Stripe (on mobile renders FIRST so the CTA is above the fold) */}
      <div className="order-1 lg:order-2 lg:flex-1 lg:flex lg:items-center lg:justify-center px-6 py-12 lg:px-16">
        <Card className="w-full max-w-md mx-auto bg-[#0a0c10]/80 backdrop-blur-sm border-white/[0.08]">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <h2 className="text-heading-2 font-extrabold text-white mb-3">
                Załóż konto i przejdź do płatności
              </h2>
              <p className="text-white/50 text-sm">
                Po założeniu konta od razu wylądujesz na bezpiecznej stronie Stripe.
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/70">
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="bg-white/[0.06] border-white/[0.1] text-white"
                  placeholder="ty@firma.pl"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-white/70">
                  Hasło
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    className="bg-white/[0.06] border-white/[0.1] text-white pr-10"
                    placeholder="Min. 8 znaków"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-white/70">
                  Powtórz hasło
                </Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required
                  className="bg-white/[0.06] border-white/[0.1] text-white"
                  placeholder="Powtórz hasło"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowReferral(!showReferral)}
                className="text-sm text-white/50 hover:text-white/70 flex items-center gap-1"
              >
                {showReferral ? (
                  <ChevronUp className="w-3 h-3" />
                ) : (
                  <ChevronDown className="w-3 h-3" />
                )}{" "}
                Mam kod polecający
              </button>
              {showReferral && (
                <Input
                  name="referralCode"
                  value={formData.referralCode}
                  onChange={handleInputChange}
                  className="bg-white/[0.06] border-white/[0.1] text-white"
                  placeholder="Kod polecający"
                />
              )}
              <Button
                type="submit"
                disabled={isRegisterLoading}
                className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white h-11 text-base font-semibold"
              >
                {isRegisterLoading ? "Tworzę konto…" : "Przejdź do płatności"}
                {!isRegisterLoading && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </form>
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-white/[0.1]" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-[#0a0c10] px-2 text-white/40">lub</span>
                </div>
              </div>
              <div className="mt-4">
                <GoogleLoginButton skipOnboardingRedirect onSuccess={() => void startCheckout()} />
              </div>
            </div>
            <p className="mt-6 text-center text-sm text-white/40">
              Masz już konto?{" "}
              <button
                type="button"
                onClick={() => setLocation("/")}
                className="text-red-400 hover:text-red-300 font-medium"
              >
                Zaloguj się
              </button>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
